<template>
	<div class="cog-choice-image-container">
		<c-generated-avatar v-if="option.svg" :svg="option.svg" />
		<img
			v-else
			:srcset="!isSvgFileType ? getSrcSet : null"
			:src="getSrc"
			:alt="option.displayValue"
			:width="getDimensions.width"
			:height="getDimensions.height"
			:class="getClasses"
			loading="lazy"
		/>
	</div>
</template>

<script lang="ts">
	import CGeneratedAvatar from './GeneratedAvatar.vue';
	import { computed, defineComponent } from 'vue';

	export type Option = {
		svg?: string;
		thumbnailImage?: string;
		displayValue?: string;
		choiceThumbnailImage?: string;
		choiceImageDimensions?: { width: number; height: number };
		choiceFullSizeImage?: string;
		fullSizeImage?: string;
	};

	export default defineComponent({
		name: 'CChoiceImage',
		components: { CGeneratedAvatar },
		props: {
			option: { type: Object as () => Option, required: true },
			isPersonField: { type: Boolean, required: true },
			choiceImageSize: { type: Number, required: true },
			fontSize: { type: Number, required: false, default: 14 }
		},
		setup(props: { option: Option; isPersonField: boolean; choiceImageSize: number; fontSize: number }) {
			const isSvgFileType = computed(() => {
				const image = props.option.choiceFullSizeImage || props.option.fullSizeImage;
				return image?.split('ct=image%2F')[1]?.includes('svg') ?? false;
			});

			const srcSetSizes = computed(() => {
				if (props.choiceImageSize === 0)
					return ['s', 's', 's'];
				else if (props.choiceImageSize === 1)
					if (props.fontSize < 15)
						return ['s', 's', 'l'];
					else
						return ['s', 'l', 'l'];
				else if (props.choiceImageSize === 2 && props.fontSize < 13)
					return ['s', 's', 'l'];
				else
					return ['l', 'l', 'l'];
			});

			const getSrcSet = computed(() => {
				if (!props.option.choiceThumbnailImage && !props.option.thumbnailImage)
					return;
				const sizes = {
					s: props.option.choiceThumbnailImage || props.option.thumbnailImage,
					l: props.option.choiceFullSizeImage || props.option.fullSizeImage
				};
				const srcSet = [];
				srcSetSizes.value.forEach((s, i) => srcSet.push(sizes[s] + ` ${i + 1}x`));
				return srcSet.join(', ');
			});

			const getSrc = computed(() => {
				return props.option.choiceThumbnailImage || props.option.thumbnailImage || null;
			});

			const getDimensions = computed(() => {
				if (props.option.choiceImageDimensions)
					return { width: props.option.choiceImageDimensions.width, height: props.option.choiceImageDimensions.height };
				else
					return props.isPersonField ? { width: 50, height: 50 } : { width: null, height: null };
			});

			const getClasses = computed(() => {
				return {
					'cog-choice-image': props.option.choiceThumbnailImage,
					'cog-lookup-image': !props.option.choiceThumbnailImage,
					'cog-avatar': props.isPersonField
				};
			});

			return {
				isSvgFileType,
				getSrcSet,
				getSrc,
				getDimensions,
				getClasses
			};
		}
	});
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-choice-image-container {
		display: flex;
		align-items: center;
		justify-content: center;
		max-width: var(--image-size);
		min-height: 1.7em;
		max-height: var(--image-size);

		img {
			display: inline-block;
			flex-shrink: 0;
			max-height: var(--image-size);
			border-radius: var(--input__border-radius);
			overflow: hidden;
			object-fit: contain;
			vertical-align: middle;
		}
	}
}
</style>